/**
 * РАСКЛАДКА ГРАФА СВОЕЙ ВОРОНКИ (WEB-2997) — хранилище-спутник редактора под
 * ключом `editorGraph`. Отдельно от `editorSlice.ts` (у него потолок длины) и
 * отдельно от черновика: перетаскивание узла не делает черновик изменённым и
 * не запускает его автосохранение, у раскладки своя запись.
 *
 * Как пишется. Места кладутся на экран сразу; в запись уходят только
 * изменения (`pending`), сервер сливает их по экранам — двое двигают разные
 * узлы и не затирают друг друга. Ответ сервера — правда со всеми
 * перетаскиваниями коллег; поверх него остаётся то, что человек успел
 * передвинуть, пока шла запись. «Авторасставить» и переключатель направления
 * при ручной расстановке — полная замена (`replace`) с отменой: отмена кладёт
 * прежние места тоже полной заменой. Паузу перед записью и повтор держит
 * обёртка графа (`useGraphLayoutSync`).
 *
 * Сбой сети или сервера — изменения возвращаются в очередь, состояние
 * `retry` (тихая строка «повторю»). Окончательный отказ (400/403/404 —
 * неверная форма, нет права, «только чтение», воронки нет) — состояние
 * `failed` без повтора: повтор там ничего не изменит.
 */
import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { graphLayoutApi, type GraphLayoutPatchBody, type GraphPieceKind } from '@/api/graphLayout';
import { extractApiError } from '@/api/errorStatus';
import { resetEditor } from '@/store/editorSlice';

export type GraphDirection = 'tb' | 'lr';
export type GraphNodePositions = Record<string, { x: number; y: number }>;

export interface GraphLayoutTarget {
  kind: GraphPieceKind;
  projectId: string;
  id: string;
}

export type GraphLayoutSaveState = 'idle' | 'saving' | 'retry' | 'failed';

interface Inflight {
  nodes: GraphNodePositions;
  replace: boolean;
  directionDirty: boolean;
}

export interface GraphLayoutState {
  target: GraphLayoutTarget | null;
  loaded: boolean;
  nodes: GraphNodePositions;
  direction: GraphDirection | null;
  /** Места, ещё не ушедшие на сервер (слияние по экранам). */
  pending: GraphNodePositions;
  /** Следующая запись — полная замена мест (авторасставить или его отмена). */
  replace: boolean;
  directionDirty: boolean;
  inflight: Inflight | null;
  saveState: GraphLayoutSaveState;
  /** Что вернуть по «Отменить» после авторасстановки. */
  undo: { nodes: GraphNodePositions; direction: GraphDirection | null } | null;
}

export const initialGraphLayoutState: GraphLayoutState = {
  target: null,
  loaded: false,
  nodes: {},
  direction: null,
  pending: {},
  replace: false,
  directionDirty: false,
  inflight: null,
  saveState: 'idle',
  undo: null,
};

const sameTarget = (a: GraphLayoutTarget | null, b: GraphLayoutTarget) =>
  !!a && a.kind === b.kind && a.projectId === b.projectId && a.id === b.id;

/** Есть ли что записать. */
export function hasGraphLayoutWork(s: GraphLayoutState): boolean {
  return s.replace || s.directionDirty || Object.keys(s.pending).length > 0;
}

/**
 * Тело записи из состояния: что уходит на сервер. `work` — либо запись в
 * полёте (санк записи), либо ещё не отправленное (уход со страницы).
 */
export function buildGraphLayoutPatch(
  s: GraphLayoutState,
  work: Pick<Inflight, 'nodes' | 'replace' | 'directionDirty'>,
): GraphLayoutPatchBody {
  const body: GraphLayoutPatchBody = {};
  if (work.replace) {
    body.nodes = s.nodes;
    body.replace = true;
  } else if (Object.keys(work.nodes).length > 0) {
    body.nodes = work.nodes;
  }
  if (work.directionDirty) body.direction = s.direction;
  return body;
}

/**
 * Уход со страницы редактора (находка приёмки 09.10): страница сбрасывает
 * редактор при размонтировании РАНЬШЕ, чем граф успевает отправить
 * несохранённое, — срез к этому моменту уже пуст. Поэтому граф отправляет
 * СНИМОК состояния, взятый до ухода, напрямую, мимо хранилища. Отказ
 * проглатывается: человек уже ушёл, показать строку негде.
 */
export async function sendGraphLayoutSnapshot(
  snapshot: GraphLayoutState,
): Promise<void> {
  const target = snapshot.target;
  if (!target || !snapshot.loaded || !hasGraphLayoutWork(snapshot)) return;
  const body = buildGraphLayoutPatch(snapshot, {
    nodes: snapshot.pending,
    replace: snapshot.replace,
    directionDirty: snapshot.directionDirty,
  });
  try {
    await graphLayoutApi.patch(target.kind, target.projectId, target.id, body);
  } catch {
    // страница закрыта — сообщить некому
  }
}

/** Окончательные отказы: повтор не поможет. */
const PERMANENT = new Set([400, 401, 403, 404, 422]);

export const loadGraphLayout = createAsyncThunk(
  'editorGraph/load',
  async (target: GraphLayoutTarget) => {
    const res = await graphLayoutApi.get(target.kind, target.projectId, target.id);
    return res.data;
  },
);

export const flushGraphLayout = createAsyncThunk<
  { target: GraphLayoutTarget; data: Awaited<ReturnType<typeof graphLayoutApi.patch>>['data'] },
  void,
  { state: { editorGraph: GraphLayoutState }; rejectValue: { target: GraphLayoutTarget; permanent: boolean } }
>(
  'editorGraph/flush',
  async (_: void, { getState, rejectWithValue }) => {
    const s = getState().editorGraph;
    const target = s.target as GraphLayoutTarget;
    const body = buildGraphLayoutPatch(s, s.inflight as Inflight);
    try {
      const res = await graphLayoutApi.patch(target.kind, target.projectId, target.id, body);
      return { target, data: res.data };
    } catch (error: unknown) {
      const { status } = extractApiError(error);
      return rejectWithValue({ target, permanent: status !== null && PERMANENT.has(status) });
    }
  },
  {
    condition: (_: void, { getState }) => {
      const s = getState().editorGraph;
      return !!s.target && s.loaded && !s.inflight && hasGraphLayoutWork(s);
    },
  },
);

const slice = createSlice({
  name: 'editorGraph',
  initialState: initialGraphLayoutState,
  reducers: {
    nodesMoved(state, action: PayloadAction<GraphNodePositions>) {
      Object.assign(state.nodes, action.payload);
      Object.assign(state.pending, action.payload);
      state.undo = null;
      if (state.saveState === 'failed') state.saveState = 'idle';
    },
    autoArranged(state, action: PayloadAction<{ direction?: GraphDirection }>) {
      state.undo = { nodes: state.nodes, direction: state.direction };
      state.nodes = {};
      state.pending = {};
      state.replace = true;
      if (action.payload.direction) {
        state.direction = action.payload.direction;
        state.directionDirty = true;
      }
      if (state.saveState === 'failed') state.saveState = 'idle';
    },
    arrangeUndone(state) {
      if (!state.undo) return;
      state.nodes = state.undo.nodes;
      state.direction = state.undo.direction;
      state.pending = {};
      state.replace = true;
      state.directionDirty = true;
      state.undo = null;
    },
    directionChanged(state, action: PayloadAction<GraphDirection>) {
      state.direction = action.payload;
      state.directionDirty = true;
    },
  },
  extraReducers: (b) => {
    b.addCase(resetEditor, () => initialGraphLayoutState);
    b.addCase(loadGraphLayout.pending, (state, action) => {
      if (sameTarget(state.target, action.meta.arg)) return;
      return { ...initialGraphLayoutState, target: action.meta.arg };
    });
    b.addCase(loadGraphLayout.fulfilled, (state, action) => {
      if (!sameTarget(state.target, action.meta.arg)) return;
      state.loaded = true;
      if (!state.replace) state.nodes = { ...action.payload.nodes, ...state.pending };
      if (!state.directionDirty) state.direction = action.payload.direction ?? null;
    });
    b.addCase(loadGraphLayout.rejected, (state, action) => {
      // без сохранённых мест граф авторасставляется — ошибку не показываем
      if (sameTarget(state.target, action.meta.arg)) state.loaded = true;
    });
    b.addCase(flushGraphLayout.pending, (state) => {
      state.inflight = {
        nodes: state.pending,
        replace: state.replace,
        directionDirty: state.directionDirty,
      };
      state.pending = {};
      state.replace = false;
      state.directionDirty = false;
      state.saveState = 'saving';
    });
    b.addCase(flushGraphLayout.fulfilled, (state, action) => {
      state.inflight = null;
      state.saveState = 'idle';
      if (!sameTarget(state.target, action.payload.target)) return;
      if (!state.replace) state.nodes = { ...action.payload.data.nodes, ...state.pending };
      if (!state.directionDirty) state.direction = action.payload.data.direction ?? null;
    });
    b.addCase(flushGraphLayout.rejected, (state, action) => {
      const inflight = state.inflight;
      state.inflight = null;
      if (!inflight || !action.payload || !sameTarget(state.target, action.payload.target)) {
        state.saveState = 'idle';
        return;
      }
      if (action.payload.permanent) {
        state.saveState = 'failed';
        return;
      }
      state.pending = { ...inflight.nodes, ...state.pending };
      state.replace = state.replace || inflight.replace;
      state.directionDirty = state.directionDirty || inflight.directionDirty;
      state.saveState = 'retry';
    });
  },
});

export const { nodesMoved, autoArranged, arrangeUndone, directionChanged } = slice.actions;
export default slice.reducer;
